import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import PropertyCardSettingsModal from "./PropertyCardSettingsModal";
import LegacyLocationUpgradeNotice from "../../geo/widgets/LegacyLocationUpgradeNotice";
import {
  getPropertyStatusLabel,
  getPropertyStatusTone,
} from "../../realty/lib/propertyLifecycle";
import { getPropertyCardAttributes } from "../lib/getPropertyCardAttributes";
import { formatCooperationBadgeText } from "../../realty/lib/cooperationOffer";
import { buildCompactLocationLine } from "../../geo/lib/locationPresentation";

// PropertyCard — feature-local canonical card-content surface.
//
// IMPORTANT (per R1 audit + R2 brief):
//   * This is NOT a PropertyCard framework.
//   * This is NOT a registry.
//   * This is NOT a reusable card engine.
//   * The class names (.card-content, .card-user, .card-carousel,
//     .card-badges, .card-media-footer, .card-like, .card-price-row,
//     .card-title, .card-body, .card-text, .address, .main-attributes,
//     .card-programs, .card-btn) are owned by modernize.css and MUST
//     appear here exactly as in design/index.html / search.html /
//     saved.html / profile.html.
//
// Ownership context is selector-free and dispatch-free; semantic weight
// (whose listing, what actions, what cohort) is owned by the
// consuming widget and passed in as props. See "Semantic Rendering
// Doctrine" + "Presentation Primitive Rules" in CLAUDE.md.
//
// Action set differs per consuming surface (index/search/saved/
// profile-objects), but the card DOM is identical.
//
// Backend reconciliation notes:
//   * `main_image` comes from PropertyListSerializer.main_image; the
//     full multi-image `images` collection is detail-only. The single
//     main image renders as the only carousel slide and the .splide
//     envelope is preserved structurally so detail.html conventions
//     stay reachable when multi-image transport lands.
//   * `images_count` / `plans_count` / `likes_count` are exposed on
//     PropertyListSerializer (R7.9a). The media footer + heart counter
//     hydrate directly from the list payload — no client derivation.
//   * Per-m² price is NOT exposed on the list serializer. No empty
//     em-dash placeholder is rendered; the only right-side financial
//     note is an enabled cooperation offer via formatCooperationBadgeText.
//   * `card-flexible` is a structural-only modifier (no CSS rules in
//     modernize.css). Saved surface passes `flexible` so the class is
//     preserved on the DOM even though it has no current effect.
//   * `created_by`, `liked_by_me`, `bookmarked_by_me` are exposed on
//     PropertyListSerializer (backend reconciliation mini-phase). The
//     canonical author strip + engagement icons hydrate directly from
//     the list payload — no client-side derivation, no compensation.
//
// Modal IDs are unique per card instance (constructed from
// property.id). Topology-local; no global modal host, no portal,
// no registry (per R2 STEP R2.9).

const PROPERTY_TYPE_LABELS = {
  apartment: "Квартира",
  house: "Будинок",
  land: "Земельна ділянка",
  commercial: "Комерційна",
  industrial: "Промислова",
};

const PROPERTY_TYPE_ICONS = {
  apartment: "fa-solid fa-building",
  house: "fa-solid fa-house-chimney",
  land: "fa-solid fa-mountain-sun",
  commercial: "fa-solid fa-store",
  industrial: "fa-solid fa-industry",
};

const DEAL_TYPE_LABELS = {
  sale: "Продаж",
  rent: "Оренда",
};

const CURRENCY_GLYPH = {
  USD: "$",
  EUR: "€",
  UAH: "₴",
};

const formatPrice = (amount, currency) => {
  if (amount === null || amount === undefined || amount === "") return null;
  const numeric = typeof amount === "number" ? amount : Number(amount);
  if (!Number.isFinite(numeric)) return null;
  const formatted = numeric
    .toLocaleString("uk-UA", { maximumFractionDigits: 2 })
    .replace(/ /g, " ");
  const glyph = CURRENCY_GLYPH[currency] || "";
  return `${glyph}${formatted}`.trim();
};

const buildAddressLine = (location) => buildCompactLocationLine(location);

const FALLBACK_IMAGE = "/images/no-photo.png";
const FALLBACK_OWNER_AVATAR = "/images/no-profile-picture.png";

function PropertyCard({
  property,
  flexible = false,
  detailHref,
  ownerHref,
  liked = false,
  bookmarked = false,
  likePending = false,
  bookmarkPending = false,
  onToggleLike,
  onToggleBookmark,
  menuItems = [],
  showLocationUpgradeNotice = false,
}) {
  // #1 (RIC-3.2) — Splide core CSS sets `.splide { visibility: hidden }`
  // and only reveals `.splide.is-initialized`. The card image was hidden
  // because this carousel was never mounted. Mount a single-slide Splide
  // (visual-only, no nav) so the image becomes visible. Same proven
  // pattern as DetailGallery; window.Splide is the global CDN build.
  // Hooks must precede the early return below.
  const carouselRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.Splide) return undefined;
    const el = carouselRef.current;
    if (!el) return undefined;
    const splide = new window.Splide(el, {
      type: "fade",
      rewind: true,
      pagination: false,
      arrows: false,
      drag: false,
    });
    splide.mount();
    return () => {
      try {
        splide.destroy(true);
      } catch (_err) {
        /* DOM already detached by React unmount */
      }
    };
    // Mount-once: the card renders a single stable main_image slide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!property) return null;

  const id = property.id;
  const status = property.status || "active";
  const dealType = property.deal_type || "sale";
  const propertyType = property.property_type || "apartment";

  const statusLabel = getPropertyStatusLabel(status);
  const statusTone = getPropertyStatusTone(status);
  const dealLabel = DEAL_TYPE_LABELS[dealType] || dealType;
  const typeLabel = PROPERTY_TYPE_LABELS[propertyType] || propertyType;
  const typeIcon = PROPERTY_TYPE_ICONS[propertyType] || "fa-solid fa-house";

  // Author strip — canonical .card-user. `created_by` is now on the
  // list payload (UserShortSerializer); a defensive null check guards
  // against future serializer changes but is not expected to trip in
  // practice. UserShortSerializer.get_fullname already falls back to
  // handle/email server-side, so the empty-name branch is rare.
  const ownerData = property.created_by || null;
  const ownerNameRaw = ownerData ? String(ownerData.fullname || "").trim() : "";
  const ownerName = ownerNameRaw || "Користувач";
  const ownerAvatar = (ownerData && ownerData.user_image) || FALLBACK_OWNER_AVATAR;
  // Profile href is owner-supplied or falls back to ownerless `#` so
  // modernize.css hover affordance still renders without breaking
  // navigation contracts. Per R-I in R1 audit: do not silently invent
  // a /user/<id> path here when ownerHref is absent — that's a
  // routing decision the consuming surface owns.
  const safeOwnerHref = ownerHref || "#";

  const mainImage = property.main_image || FALLBACK_IMAGE;
  const priceText = formatPrice(property.price, property.currency);
  const addressLine = buildAddressLine(property.location);
  const cardAttributes = getPropertyCardAttributes(property);

  const cardClass = flexible
    ? "card-content card-flexible card"
    : "card-content card";

  const settingsModalId = `card-settings-${id}`;

  // Programs slot — backend transport: has_housing_program +
  // has_recovery_program on PropertyListSerializer.
  const programs = [];
  if (property.has_housing_program) {
    programs.push({
      key: "housing",
      icon: "fa-solid fa-circle-check",
      label: "єОселя",
      highlight: true,
    });
  }
  if (property.has_recovery_program) {
    programs.push({
      key: "recovery",
      icon: "fa-solid fa-shield-heart",
      label: "єВідновлення",
      highlight: false,
    });
  }

  return (
    <>
    <article
      className={cardClass}
      data-status={status}
      data-deal-type={dealType}
      data-property-type={propertyType}
      data-property-id={id}
    >
      {/* author strip + settings entry */}
      <div className="card-user">
        <Link to={safeOwnerHref} className="content-user">
          <img
            src={ownerAvatar}
            alt=""
            className="content-user-photo"
          />
          <span>{ownerName}</span>
        </Link>
        {menuItems.length > 0 ? (
          <button
            type="button"
            className="card-action"
            data-bs-toggle="modal"
            data-bs-target={`#${settingsModalId}`}
            aria-label="Налаштування оголошення"
          >
            <i className="fa-solid fa-gear" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {/* Carousel + absolute overlays. .card-badges and .card-media-footer
          MUST remain descendants of .card-carousel — modernize.css uses
          position:absolute against the carousel's position:relative. */}
      <div className="card-carousel">
        <div className="card-badges" aria-label="Класифікація оголошення">
          <span
            className={`card-badge card-badge--status status-${statusTone}`}
          >
            <span className="dot" aria-hidden="true" />
            {statusLabel}
          </span>
          <span className="card-badge card-badge--deal">
            <i className="fa-solid fa-tag" aria-hidden="true" />
            {dealLabel}
          </span>
          <span className="card-badge card-badge--type">
            <i className={typeIcon} aria-hidden="true" />
            {typeLabel}
          </span>
        </div>

        {/* The design uses a Splide carousel with multiple slides.
            PropertyListSerializer exposes only `main_image`. Render a
            single .splide envelope around the main image so the
            modernize.css selectors that key on `.card-carousel .splide__*`
            still resolve. Future multi-image transport will hydrate
            additional .splide__slide entries.
            TODO: backend transport missing — PropertyListSerializer
            does not expose the full `images` collection. */}
        <section
          ref={carouselRef}
          className="splide"
          aria-label="Фото об'єкта"
        >
          <div className="splide__track">
            <ul className="splide__list">
              <li className="splide__slide">
                <img
                  src={mainImage}
                  alt={property.title || "Фото нерухомості"}
                />
              </li>
            </ul>
          </div>
        </section>

        {(property.images_count > 0 || property.plans_count > 0) ? (
          <div className="card-media-footer" aria-label="Медіа об'єкта">
            {property.images_count > 0 ? (
              <span className="card-media-meta">
                <i className="fa-regular fa-images" aria-hidden="true" />
                <span>{property.images_count}</span>
              </span>
            ) : null}
            {property.plans_count > 0 ? (
              <span className="card-media-meta">
                <i className="fa-solid fa-vector-square" aria-hidden="true" />
                <span>{property.plans_count}</span>
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* engagement strip — like + bookmark.
          Direct-act buttons (no modal). Topology-local; no
          engagement cache, no optimistic store. Liked / bookmarked
          flags are owned by the parent screen. */}
      <div className="card-like">
        <div className="card-like__engagement">
          {/* #6 (RIC-3.2) — canonical .heart (styles.css) is a checkbox +
              SVG toggle; the previous <button><i> rendered none of the
              .heart rules. Controlled: `checked={liked}`, onChange fires the
              parent like handler; the :checked CSS swaps outline→filled. */}
          <div className="heart">
            <input
              type="checkbox"
              className="checkbox"
              checked={liked}
              onChange={onToggleLike}
              disabled={likePending || typeof onToggleLike !== "function"}
              aria-busy={likePending ? "true" : undefined}
              aria-label={liked ? "Прибрати з вподобань" : "Подобається"}
            />
            <div className="svg-container" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                className="svg-outline"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M17.5,1.917a6.4,6.4,0,0,0-5.5,3.3,6.4,6.4,0,0,0-5.5-3.3A6.8,6.8,0,0,0,0,8.967c0,4.547,4.786,9.513,8.8,12.88a4.974,4.974,0,0,0,6.4,0C19.214,18.48,24,13.514,24,8.967A6.8,6.8,0,0,0,17.5,1.917Zm-3.585,18.4a2.973,2.973,0,0,1-3.83,0C4.947,16.006,2,11.87,2,8.967a4.8,4.8,0,0,1,4.5-5.05A4.8,4.8,0,0,1,11,8.967a1,1,0,0,0,2,0,4.8,4.8,0,0,1,4.5-5.05A4.8,4.8,0,0,1,22,8.967C22,11.87,19.053,16.006,13.915,20.313Z" />
              </svg>
              <svg
                viewBox="0 0 24 24"
                className="svg-filled"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M17.5,1.917a6.4,6.4,0,0,0-5.5,3.3,6.4,6.4,0,0,0-5.5-3.3A6.8,6.8,0,0,0,0,8.967c0,4.547,4.786,9.513,8.8,12.88a4.974,4.974,0,0,0,6.4,0C19.214,18.48,24,13.514,24,8.967A6.8,6.8,0,0,0,17.5,1.917Z" />
              </svg>
              <svg
                className="svg-celebrate"
                width="100"
                height="100"
                xmlns="http://www.w3.org/2000/svg"
              >
                <polygon points="10,10 20,20" />
                <polygon points="10,50 20,50" />
                <polygon points="20,80 30,70" />
                <polygon points="90,10 80,20" />
                <polygon points="90,50 80,50" />
                <polygon points="80,80 70,70" />
              </svg>
            </div>
          </div>
          {typeof property.likes_count === "number" ? (
            <span className="card-like__count">
              {property.likes_count} вподобань
            </span>
          ) : (
            <span className="card-like__count muted">—</span>
          )}
        </div>

        <button
          type="button"
          className={`ui-bookmark like ms-auto ${
            bookmarked ? "is-bookmarked" : ""
          }`}
          onClick={onToggleBookmark}
          disabled={bookmarkPending || typeof onToggleBookmark !== "function"}
          aria-busy={bookmarkPending ? "true" : undefined}
          aria-label={
            bookmarked ? "Прибрати зі збережених" : "Зберегти оголошення"
          }
          aria-pressed={bookmarked}
        >
          <span className="bookmark" aria-hidden="true">
            <i
              className={
                bookmarked ? "fa-solid fa-bookmark" : "fa-regular fa-bookmark"
              }
            />
          </span>
        </button>
      </div>

      {/* Price row — main on left, side on right.
          Both wrappers must remain present; modernize.css responsive
          breakpoint at 768px relies on flex-direction:column on both. */}
      <div className="card-price-row">
        <div className="card-price-main">
          {priceText ? (
            <span className="card-price-value">{priceText}</span>
          ) : (
            <span className="card-price-value muted">—</span>
          )}
          {property.currency ? (
            <span className="card-price-currency">{property.currency}</span>
          ) : null}
        </div>
        <div className="card-price-side">
          {/* Per-m² transport is absent — do not render an empty dash.
              Cooperation note only when API exposes an enabled offer. */}
          {(() => {
            const badge = formatCooperationBadgeText(
              property.cooperation_offer,
              property,
            );
            if (!badge) return null;
            return (
              <span className="card-price-note">
                <span>{badge}</span>
              </span>
            );
          })()}
        </div>
      </div>

      {/* #3 (RIC-3.2) — plain title text. The design title is not a link;
          wrapping it in <Link> made the inner <a> inherit Bootstrap's link
          color instead of .card-title's --color-text-primary. Detail
          navigation lives on the "Детальніше" button below. */}
      <h2 className="card-title">{property.title || "Без назви"}</h2>

      {showLocationUpgradeNotice ? (
        <LegacyLocationUpgradeNotice
          propertyId={id}
          location={property.location}
          variant="compact"
        />
      ) : null}

      <div className="card-body">
        <div className="card-text">
          {addressLine ? (
            <div className="address">{addressLine}</div>
          ) : (
            <div className="address">
              <span className="muted">—</span>
            </div>
          )}

          {/* Type-aware key facts (Issue #181) — at most 3 non-empty pills. */}
          {cardAttributes.length > 0 ? (
            <div className="main-attributes" aria-label="Ключові характеристики">
              {cardAttributes.map((attr) => (
                <div className="attribute" key={attr.key}>
                  <i className={attr.icon} aria-hidden="true" />
                  <span>{attr.label}</span>
                </div>
              ))}
            </div>
          ) : null}

          {programs.length > 0 ? (
            <div className="card-programs" aria-label="Державні програми">
              {programs.map((program) => (
                <span
                  key={program.key}
                  className={
                    program.highlight
                      ? "card-program card-program--highlight"
                      : "card-program"
                  }
                >
                  <i className={program.icon} aria-hidden="true" />
                  {program.label}
                </span>
              ))}
            </div>
          ) : null}

          <div className="card-btn">
            <Link to={detailHref || "#"} className="action-btn">
              Детальніше
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </article>
    {/* #5 (RIC-3.2) — the settings modal is rendered as a SIBLING of the
        article, not a descendant. `.card-content:hover` applies
        transform: translateY(-4px) (modernize.css), which makes the card a
        containing block for position:fixed children — trapping the Bootstrap
        modal behind the full-screen backdrop while hovering (exactly when
        the gear is clicked). data-bs-target finds the modal by id anywhere
        in the document, so moving it out fixes the broken open/loop without
        any CSS change. */}
    {menuItems.length > 0 ? (
      <PropertyCardSettingsModal id={settingsModalId} items={menuItems} />
    ) : null}
    </>
  );
}

export default PropertyCard;
